import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll position on route changes.
 * - New pathname → scroll to top (no animation, instant)
 * - Same pathname + hash (#section) → smooth-scroll to the element
 * - Same pathname, no hash (state update only) → leave scroll alone
 *
 * Mount once inside <BrowserRouter> in App.js.
 */
export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer until target node is mounted
      requestAnimationFrame(() => {
        const id = decodeURIComponent(hash.slice(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo(0, 0);
        }
      });
      return;
    }

    // Instant page-top reset on route change. The global
    // `html { scroll-behavior: smooth }` would otherwise animate this and
    // can leave the page mid-scroll if the prior offset was large.
    const html = document.documentElement;
    html.style.setProperty("scroll-behavior", "auto");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    // Restore on next frame so subsequent in-page anchor clicks remain smooth.
    requestAnimationFrame(() => {
      html.style.removeProperty("scroll-behavior");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash, key]);

  return null;
}
